import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from "@mui/material";
import { get, post } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Types
========================= */
interface Vendor {
  vendorId: string;
  vendorName: string;
}

interface AddZoneProps {
  handleClose: () => void;
  fetchZones: () => void;
  selectedVendorId: string; // Add this prop
}

const BoardAddZone: React.FC<AddZoneProps> = ({
  handleClose,
  fetchZones,
  selectedVendorId // Receive the selected vendor ID
}) => {
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    vendorId: selectedVendorId || "", // Initialize with passed vendor ID
    name: "",
    isActive: true
  });

  /* =========================
     Fetch Vendors (for display purposes only)
  ========================= */
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await get(`${APIURL}/api/vendors`);
        setVendors(res?.data || res);
      } catch {
        showSnackbar("Failed to load vendors", "error");
      }
    };

    fetchVendors();
  }, [APIURL, showSnackbar]);

  /* =========================
     Update form when selectedVendorId prop changes
  ========================= */
  useEffect(() => {
    setForm(prev => ({ ...prev, vendorId: selectedVendorId }));
  }, [selectedVendorId]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.vendorId || !form.name) {
      showSnackbar("Vendor and Zone Name are required", "warning");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id: 0,
        vendorId: form.vendorId,
        name: form.name,
        isActive: form.isActive
      };

      const response = await post(
        `${APIURL}/api/miscellaneous/zone`,
        payload
      );

      if (response?.statusCode === 200) {
        showSnackbar(
          response?.message || "Zone added successfully",
          "success"
        );
        fetchZones();
        handleClose();
      } else {
        showSnackbar(
          response?.message || "Failed to add zone",
          "error"
        );
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to add zone", "error");
    } finally {
      setLoading(false);
    }
  };

  // Find the selected vendor name for display
  const selectedVendor = vendors.find(v => v.vendorId === selectedVendorId);

  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Add Zone
      </Typography>

      <Grid container spacing={2}>
        {/* Vendor Display - Read Only */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="vendor-label">Vendor</InputLabel>
            <Select
              labelId="vendor-label"
              label="Vendor"
              value={selectedVendorId || ""}
              disabled
              inputProps={{ readOnly: true }}
            >
              <MenuItem value={selectedVendorId || ""}>
                {selectedVendor?.vendorName || selectedVendorId || "Select Vendor"}
              </MenuItem>
            </Select>
            <FormHelperText>Vendor is selected from the parent list</FormHelperText>
          </FormControl>
          <input type="hidden" name="vendorId" value={form.vendorId} />
        </Grid>

        {/* Zone Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Zone Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>

        {/* Active Switch */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BoardAddZone;